import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Modal } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getPblCase,
  getProBonoDirectoryLawyers,
  manualInvitePblLawyer,
  preferPblLawyer,
  unpreferPblLawyer,
} from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import { Search } from '@/components/Icon';
import { SearchIconButton, SearchInput } from '@/components/Pbl/CaseList/styles';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import type { PblCase } from '@/types/pbl';
import { translatePblDisplayList, translatePblDisplayValue } from '@/utils/pblDisplayTranslation';
import { errorHandler, successHandler } from '@/utils/toastHandler';

import {
  buildManualInvitePayload,
  canManualInvitePblCase,
  filterPblLawyers,
  isValidLawyerChoiceReason,
  mapDirectoryEntryToOcLawyer,
} from './caseMatchingState';
import LawyerReviewSummaryModal from './LawyerReviewSummaryModal';
import {
  ChoiceWarningNotice,
  CvTitle,
  ChooseButtonWrap,
  FieldErrorText,
  InfoLabel,
  InfoValue,
  LawyerCardActions,
  LawyerDivider,
  LawyerFilterRow,
  LawyerFilterSelect,
  LawyerFilterSelectWrap,
  LawyerListCard,
  LawyerListPanel,
  LawyerListShell,
  LawyerNameRow,
  LawyerMetaGrid,
  LawyerName,
  LawyerSearchGroup,
  ModalAction,
  ModalSurface,
  ModalText,
  ModalTitle,
  ReasonLabel,
  ReasonTextarea,
  PreferredLawyerBadge,
  PreferredLawyerButton,
  TitleBar,
} from './styles';
import type { PblOcLawyer, PblOcLawyerFilters } from './types';
import { useTranslation } from 'react-i18next';

const qualificationOptions = ['all', 'Hong Kong Solicitor', 'Qualified GBA Lawyer'];
const languageOptions = ['all', 'English', 'Cantonese', 'Other'];
const practiceAreaOptions = [
  'all',
  'Estate Planning',
  'Family matters',
  'Employment disputes',
  'Building management',
  'Tenancy and properties related matters',
  'Criminal matters',
];

const optionLabel = (t: (key: string, options?: Record<string, unknown>) => string, value: string) =>
  value === 'all'
    ? t('pbl.pagesHardcoded.roleDashboard.all', { defaultValue: 'All' })
    : translatePblDisplayValue(t, value);

const buildCaseDetailPath = (caseId: string) => PATH.pbl.caseDetail.replace(':caseId', caseId);
const ocReviewRequiredMessage = 'PBL case must pass OC review before lawyer invitation.';

const LawyerListPage = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const { isMobile } = useResize();
  const [filters, setFilters] = useState<PblOcLawyerFilters>({
    qualification: 'all',
    language: 'all',
    practiceArea: 'all',
    keyword: '',
  });
  const [lawyers, setLawyers] = useState<PblOcLawyer[]>([]);
  const [selectedLawyer, setSelectedLawyer] = useState<PblOcLawyer | null>(null);
  const [previewLawyer, setPreviewLawyer] = useState<PblOcLawyer | null>(null);
  const [previewLanguage, setPreviewLanguage] = useState<'en' | 'zh'>('en');
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [isSubmittingChoice, setIsSubmittingChoice] = useState(false);
  const [updatingPreferredLawyerId, setUpdatingPreferredLawyerId] = useState<number | null>(null);
  const [currentCase, setCurrentCase] = useState<PblCase | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadCaseAndLawyers = async () => {
      const numericCaseId = Number(caseId);
      if (!Number.isFinite(numericCaseId)) {
        errorHandler(t('pbl.pagesHardcoded.ocManageCases.lawyerList.invalidCaseId'));
        return;
      }

      try {
        const caseResponse = await getPblCase(numericCaseId);

        if (caseResponse.data.success === false) {
          errorHandler(caseResponse.data.message || t('pbl.pagesHardcoded.ocManageCases.lawyerList.failedToLoadCase'));
          return;
        }

        const loadedCase = caseResponse.data.data;
        if (!canManualInvitePblCase(loadedCase.status)) {
          errorHandler(ocReviewRequiredMessage);
          navigate(buildCaseDetailPath(String(numericCaseId)), { replace: true });
          return;
        }

        const response = await getProBonoDirectoryLawyers({ case_id: numericCaseId, per_page: 50, page: 1 });

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.lawyerList.failedToLoadLawyers'));
          return;
        }

        if (isMounted) {
          setCurrentCase(loadedCase);
          setLawyers(response.data.data.data.map(mapDirectoryEntryToOcLawyer));
        }
      } catch (error: any) {
        if (isMounted) {
          errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.ocManageCases.lawyerList.failedToLoadLawyers'));
        }
      }
    };

    loadCaseAndLawyers();

    return () => {
      isMounted = false;
    };
  }, [caseId, navigate]);

  const visibleLawyers = useMemo(() => filterPblLawyers(lawyers, filters), [filters, lawyers]);
  const reasonInvalid = reasonTouched && !isValidLawyerChoiceReason(reason);

  const updateFilter =
    (key: keyof PblOcLawyerFilters) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setFilters((prev) => ({ ...prev, [key]: event.target.value }));
    };

  const openChoiceModal = (lawyer: PblOcLawyer) => {
    setSelectedLawyer(lawyer);
    setReason('');
    setReasonTouched(false);
  };

  const closeChoiceModal = () => {
    setSelectedLawyer(null);
    setReason('');
    setReasonTouched(false);
  };

  const openPreviewModal = (lawyer: PblOcLawyer, language: 'en' | 'zh') => {
    setPreviewLawyer(lawyer);
    setPreviewLanguage(language);
  };

  const togglePreferredLawyer = async (lawyer: PblOcLawyer) => {
    const numericCaseId = Number(caseId);
    const directoryEntryId = lawyer.directoryEntryId;

    if (!Number.isFinite(numericCaseId) || !directoryEntryId || updatingPreferredLawyerId !== null) return;

    setUpdatingPreferredLawyerId(lawyer.id);

    try {
      const response = lawyer.isPersonalFavorite
        ? await unpreferPblLawyer(numericCaseId, directoryEntryId)
        : await preferPblLawyer(numericCaseId, directoryEntryId);
      const isPreferred = response.data.data?.is_preferred === true;
      const isPersonalFavorite = response.data.data?.is_personal_favorite === true;

      setLawyers((current) =>
        current
          .map((item) => (item.id === lawyer.id ? { ...item, isPreferred, isPersonalFavorite } : item))
          .sort((left, right) => Number(right.isPreferred) - Number(left.isPreferred))
      );
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message ||
          t('pbl.pagesHardcoded.ocManageCases.lawyerList.failedToUpdatePreferredLawyer', {
            defaultValue: 'Failed to update preferred lawyer.',
          })
      );
    } finally {
      setUpdatingPreferredLawyerId(null);
    }
  };

  const confirmChoice = async () => {
    setReasonTouched(true);

    if (!selectedLawyer || !isValidLawyerChoiceReason(reason) || isSubmittingChoice) return;

    const numericCaseId = Number(caseId);
    if (!Number.isFinite(numericCaseId)) {
      errorHandler(t('pbl.pagesHardcoded.ocManageCases.lawyerList.invalidCaseId'));
      return;
    }

    if (!canManualInvitePblCase(currentCase?.status)) {
      errorHandler(ocReviewRequiredMessage);
      navigate(buildCaseDetailPath(String(numericCaseId)), { replace: true });
      return;
    }

    setIsSubmittingChoice(true);

    try {
      const response = await manualInvitePblLawyer(numericCaseId, {
        ...buildManualInvitePayload(selectedLawyer, reason),
      });

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.lawyerList.failedToChooseLawyer'));
        return;
      }

      successHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.lawyerList.lawyerSelected'));
      navigate(`${buildCaseDetailPath(String(numericCaseId))}?lawyerId=${selectedLawyer.id}&source=manual`, {
        state: { choiceReason: reason.trim() },
      });
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.ocManageCases.lawyerList.failedToChooseLawyer'));
    } finally {
      setIsSubmittingChoice(false);
    }
  };

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button
            type="button"
            size="md"
            variant="primary"
            rounded={false}
            onClick={() => navigate(buildCaseDetailPath(String(caseId || '')))}
          >
            {t('pbl.pagesHardcoded.ocManageCases.lawyerList.back')}</Button>
          <span>
            {t('pbl.pagesHardcoded.ocManageCases.lawyerList.home')}<strong>{t('pbl.pagesHardcoded.ocManageCases.lawyerList.proBonoLegalServiceCasesReview')}</strong>
          </span>
        </TitleBar>
      }
    >
      <LawyerListShell>
        <LawyerFilterRow isMobile={isMobile}>
          <LawyerFilterSelectWrap>
            <LawyerFilterSelect
              aria-label={t('pbl.pagesHardcoded.ocManageCases.lawyerList.filterByQualification')}
              value={filters.qualification}
              onChange={updateFilter('qualification')}
            >
              {qualificationOptions.map((option) => (
                <option key={option} value={option}>
                  {optionLabel(t, option)}
                </option>
              ))}
            </LawyerFilterSelect>
          </LawyerFilterSelectWrap>
          <LawyerFilterSelectWrap>
            <LawyerFilterSelect
              aria-label={t('pbl.pagesHardcoded.ocManageCases.lawyerList.filterByLanguage')}
              value={filters.language}
              onChange={updateFilter('language')}
            >
              {languageOptions.map((option) => (
                <option key={option} value={option}>
                  {optionLabel(t, option)}
                </option>
              ))}
            </LawyerFilterSelect>
          </LawyerFilterSelectWrap>
          <LawyerFilterSelectWrap>
            <LawyerFilterSelect
              aria-label={t('pbl.pagesHardcoded.ocManageCases.lawyerList.filterByPracticeArea')}
              value={filters.practiceArea}
              onChange={updateFilter('practiceArea')}
            >
              {practiceAreaOptions.map((option) => (
                <option key={option} value={option}>
                  {optionLabel(t, option)}
                </option>
              ))}
            </LawyerFilterSelect>
          </LawyerFilterSelectWrap>
          <LawyerSearchGroup isMobile={isMobile}>
            <SearchInput
              aria-label={t('pbl.pagesHardcoded.ocManageCases.lawyerList.searchLawyers')}
              placeholder={t('pbl.pagesHardcoded.ocManageCases.lawyerList.search')}
              value={filters.keyword}
              onChange={updateFilter('keyword')}
            />
            <SearchIconButton type="button" aria-label={t('pbl.pagesHardcoded.ocManageCases.lawyerList.searchLawyers')}>
              <img src={Search.SearchSVG} alt="" />
            </SearchIconButton>
          </LawyerSearchGroup>
        </LawyerFilterRow>

        <LawyerListPanel>
          {visibleLawyers.map((lawyer) => (
            <LawyerListCard key={lawyer.id}>
              <LawyerNameRow>
                <div>
                  <LawyerName>{lawyer.name}</LawyerName>
                  {lawyer.isPreferred && (
                    <PreferredLawyerBadge>
                      {t('pbl.pagesHardcoded.ocManageCases.lawyerList.preferred', { defaultValue: 'Preferred' })}
                    </PreferredLawyerBadge>
                  )}
                </div>
                <PreferredLawyerButton
                  type="button"
                  $active={Boolean(lawyer.isPersonalFavorite)}
                  disabled={updatingPreferredLawyerId !== null}
                  aria-label={
                    lawyer.isPersonalFavorite
                      ? t('pbl.pagesHardcoded.ocManageCases.lawyerList.removePreferred', {
                          defaultValue: `Remove ${lawyer.name} from preferred lawyers`,
                        })
                      : t('pbl.pagesHardcoded.ocManageCases.lawyerList.addPreferred', {
                          defaultValue: `Add ${lawyer.name} to preferred lawyers`,
                        })
                  }
                  aria-pressed={Boolean(lawyer.isPersonalFavorite)}
                  onClick={() => togglePreferredLawyer(lawyer)}
                >
                  {lawyer.isPersonalFavorite ? '\u2605' : '\u2606'}
                </PreferredLawyerButton>
              </LawyerNameRow>
              <LawyerMetaGrid>
                <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.lawyerList.professionalQualificationCertifications')}</InfoLabel>
                <InfoValue>{translatePblDisplayList(t, lawyer.qualification)}</InfoValue>
                <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.lawyerList.admittedYears')}</InfoLabel>
                <InfoValue>
                  {lawyer.admittedYears === null ? '-' : t('pbl.lawyerMatching.caseDetail.lawyerCard.years', {
                    count: lawyer.admittedYears,
                    defaultValue: '{{count}} years',
                  })}
                </InfoValue>
                <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.lawyerList.languages')}</InfoLabel>
                <InfoValue>{translatePblDisplayList(t, lawyer.languages)}</InfoValue>
                <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.lawyerList.primaryPracticeArea')}</InfoLabel>
                <InfoValue>{translatePblDisplayValue(t, lawyer.primaryPracticeArea)}</InfoValue>
                <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.lawyerList.completedProBonoCaseS')}</InfoLabel>
                <InfoValue>{lawyer.completedCases}</InfoValue>
              </LawyerMetaGrid>
              <LawyerDivider />
              <CvTitle>{t('pbl.pagesHardcoded.ocManageCases.lawyerList.cvOfTheProbonoLawyer')}</CvTitle>
              <LawyerCardActions>
                <Button
                  type="button"
                  size="md"
                  variant="primary"
                  rounded={false}
                  onClick={() => openPreviewModal(lawyer, 'en')}
                >
                  {t('pbl.pagesHardcoded.ocManageCases.lawyerList.details')}
                </Button>
                <Button
                  type="button"
                  size="md"
                  variant="primary"
                  rounded={false}
                  onClick={() => openChoiceModal(lawyer)}
                >
                  {t('pbl.pagesHardcoded.ocManageCases.lawyerList.choose')}
                </Button>
              </LawyerCardActions>
            </LawyerListCard>
          ))}
          {visibleLawyers.length === 0 && <Text margin={0}>{t('pbl.pagesHardcoded.ocManageCases.lawyerList.noLawyersFound')}</Text>}
        </LawyerListPanel>
      </LawyerListShell>

      <Modal open={!!selectedLawyer} onClose={closeChoiceModal} aria-labelledby="choose-lawyer-title">
        <ModalSurface>
          <ModalTitle id="choose-lawyer-title">{t('pbl.pagesHardcoded.ocManageCases.lawyerList.chooseALawyer')}</ModalTitle>
          <ModalText>{t('pbl.pagesHardcoded.ocManageCases.lawyerList.pleaseEnterTheReasonWhyYouChoseThis')}</ModalText>
          <ChoiceWarningNotice role="alert">
            {t('pbl.pagesHardcoded.ocManageCases.lawyerList.onceALawyerIsSelectedThisChoiceCannot')}</ChoiceWarningNotice>
          <ReasonLabel htmlFor="choose-lawyer-reason">{t('pbl.pagesHardcoded.ocManageCases.lawyerList.reasonForChoosingThisLawyer')}</ReasonLabel>
          <ReasonTextarea
            id="choose-lawyer-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setReasonTouched(true)}
          />
          {reasonInvalid && <FieldErrorText>{t('pbl.pagesHardcoded.ocManageCases.lawyerList.pleaseEnterAReasonBeforeChoosingThisLawyer')}</FieldErrorText>}
          <ChooseButtonWrap>
            <ModalAction>
              <Button
                type="button"
                size="lg"
                variant="primary"
                rounded={false}
                disabled={isSubmittingChoice}
                onClick={confirmChoice}
              >
                {isSubmittingChoice ? t('pbl.pagesHardcoded.ocManageCases.lawyerList.choosing') : t('pbl.pagesHardcoded.ocManageCases.lawyerList.choose')}
              </Button>
              <Button type="button" size="lg" variant="primary-invert" rounded={false} onClick={closeChoiceModal}>
                {t('pbl.pagesHardcoded.ocManageCases.lawyerList.cancel')}</Button>
            </ModalAction>
          </ChooseButtonWrap>
        </ModalSurface>
      </Modal>
      <LawyerReviewSummaryModal
        lawyer={previewLawyer}
        language={previewLanguage}
        open={Boolean(previewLawyer)}
        onClose={() => setPreviewLawyer(null)}
      />
    </PageContainer>
  );
};

export default LawyerListPage;
